import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }

import { useLanguageStore } from '@/stores/LanguageStore'
import debounce from 'lodash/debounce'
import { Select, VirtualScrollerLazyEvent } from 'primevue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'

export interface LazySelectPage<T> {
  listOfItems: T[]
  page: number
  numberOfPages: number
  numberOfItems: number
}

export interface LazySelectFetchParams {
  page: number
  pageSize: number
  search: string
  signal: AbortSignal
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LazySelect',
  props: {
    modelValue: {},
    fetcher: {},
    resolveSelected: {},
    optionLabel: {},
    optionValue: {},
    filterFields: {},
    placeholder: {},
    filterPlaceholder: {},
    emptyMessage: {},
    loadingMessage: {},
    pageSize: { default: 20 },
    itemSize: { default: 40 },
    disabled: { type: Boolean },
    showClear: { type: Boolean },
    filter: { type: Boolean, default: true }
  },
  emits: ["update:modelValue", "change", "error"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const languageStore = useLanguageStore()

const items = ref<any[]>([])
const loading = ref(false)
const search = ref('')
const page = ref(1)
const numberOfPages = ref(0)
const selectedOption = ref<any>(null)

let abortController: AbortController | null = null

const selectedValue = computed({
  get: () => props.modelValue ?? null,
  set: (value) => emit('update:modelValue', value),
})

const resolveValue = (item: any) => (props.optionValue ? item?.[props.optionValue] : item)

const resolveLabel = (item: any) =>
  typeof props.optionLabel === 'function' ? props.optionLabel(item) : item?.[props.optionLabel]

const displayLabel = computed(() => {
  if (props.modelValue === null || props.modelValue === undefined) return ''

  const option =
    items.value.find((item) => resolveValue(item) === props.modelValue) ?? selectedOption.value

  if (!option || resolveValue(option) !== props.modelValue) return ''

  return resolveLabel(option)
})

async function load(targetPage: number, append: boolean) {
  if (abortController) abortController.abort()

  abortController = new AbortController()
  const signal = abortController.signal

  loading.value = true

  try {
    const result = await props.fetcher({
      page: targetPage,
      pageSize: props.pageSize,
      search: search.value,
      signal,
    })

    if (signal.aborted) return

    page.value = result?.page ?? targetPage
    numberOfPages.value = result?.numberOfPages ?? 0

    const loaded = result?.listOfItems ?? []
    items.value = append ? [...items.value, ...loaded] : loaded
  } catch (error) {
    if (signal.aborted) return

    if (!append) items.value = []
    emit('error', error)
  } finally {
    if (!signal.aborted) loading.value = false
  }
}

const onFilter = debounce((event: { value: string }) => {
  search.value = event.value ?? ''
  load(1, false)
}, 400)

function onLazyLoad(event: VirtualScrollerLazyEvent) {
  if (loading.value) return
  if (page.value >= numberOfPages.value) return
  if (event.last < items.value.length) return

  load(page.value + 1, true)
}

function onBeforeShow() {
  if (!items.value.length) load(1, false)
}

function onChange(event: { value: any }) {
  const option = items.value.find((item) => resolveValue(item) === event.value)
  if (option) selectedOption.value = option
  emit('change')
}

function onHide() {
  onFilter.cancel()
  search.value = ''
  items.value = []
  page.value = 1
  numberOfPages.value = 0
}

async function resolveSelectedOption() {
  const value = props.modelValue

  if (value === null || value === undefined) {
    selectedOption.value = null
    return
  }

  const fromItems = items.value.find((item) => resolveValue(item) === value)
  if (fromItems) {
    selectedOption.value = fromItems
    return
  }

  if (selectedOption.value && resolveValue(selectedOption.value) === value) return
  if (!props.resolveSelected) return

  try {
    const resolved = await props.resolveSelected(value)
    if (resolved && props.modelValue === value) selectedOption.value = resolved
  } catch (error) {
    emit('error', error)
  }
}

watch([() => props.modelValue, items], resolveSelectedOption, { immediate: true })

onBeforeUnmount(() => {
  onFilter.cancel()
  if (abortController) abortController.abort()
})

__expose({
  refresh: () => load(1, false),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Select), {
    pt: { label: { class: '!text-sm' } },
    modelValue: selectedValue.value,
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedValue).value = $event)),
    options: items.value,
    "option-label": __props.optionLabel,
    "option-value": __props.optionValue,
    placeholder: __props.placeholder,
    disabled: __props.disabled,
    "show-clear": __props.showClear,
    loading: loading.value && !items.value.length,
    filter: __props.filter,
    "filter-placeholder": __props.filterPlaceholder ?? _unref(languageStore).t('general.search'),
    "filter-fields": __props.filterFields,
    "empty-message": loading.value ? __props.loadingMessage : __props.emptyMessage,
    "empty-filter-message": loading.value ? __props.loadingMessage : __props.emptyMessage,
    "virtual-scroller-options": {
      lazy: true,
      onLazyLoad: onLazyLoad,
      items: items.value,
      loading: loading.value,
      itemSize: __props.itemSize,
      showLoader: true,
      appendOnly: true,
      step: __props.pageSize,
    },
    "auto-filter-focus": "",
    onFilter: _unref(onFilter),
    onBeforeShow: onBeforeShow,
    onHide: onHide,
    onChange: onChange
  }, {
    value: _withCtx((slotProps) => [
      (displayLabel.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(displayLabel.value), 1))
        : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(slotProps.placeholder), 1))
    ]),
    _: 1
  }, 8, ["modelValue", "options", "option-label", "option-value", "placeholder", "disabled", "show-clear", "loading", "filter", "filter-placeholder", "filter-fields", "empty-message", "empty-filter-message", "virtual-scroller-options", "onFilter"]))
}
}

})